// === Énumérations et constantes ===

/**
 * LANGUAGE SUPPORT NOTICE:
 * This platform now supports multiple languages for exercise generation, feedback, and chat.
 *
 * Supported languages:
 * - 'fr': French (default, all existing users)
 * - 'en': English (new optional feature)
 *
 * When adding new language support:
 * 1. Add new language code to this enum
 * 2. Add translations to shared/src/constants/translations.ts
 * 3. Update prompt templates in backend/src/services/gemini.ts
 * 4. Test all language features before deployment
 *
 * Note: UI elements remain in French. Only generated content (exercises, feedback, chat)
 * is translated based on user preference.
 */
export enum Language {
  FRENCH = 'fr',
  ENGLISH = 'en'
}

export enum LearningMode {
  UNITY_CSHARP = 'unity_csharp',
  PYTHON_JS = 'python_js',
  GODOT = 'godot'
}

export enum ProgrammingLanguage {
  CSHARP = 'csharp',
  PYTHON = 'python',
  JAVASCRIPT = 'javascript',
  GDSCRIPT = 'gdscript'
}

export enum Difficulty {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced'
}

export enum ExerciseType {
  COMPLETE_CODE = 'complete_code',
  FIX_ERRORS = 'fix_errors',
  /** G2 — exercice pré-créé rendu par le simulateur (canvas + faux compilateur), jamais généré par l'IA. */
  INTERACTIVE_SIM = 'interactive_sim'
}

export enum ConceptCategory {
  // Fondamentaux C#
  DATA_TYPES = 'data_types',
  VARIABLES = 'variables',
  ACCESS_MODIFIERS = 'access_modifiers',
  METHODS = 'methods',
  OPERATORS = 'operators',
  CONDITIONALS = 'conditionals',
  LOOPS = 'loops',
  ENUMS = 'enums',
  COMMENTS = 'comments',
  ERROR_IDENTIFICATION = 'error_identification',
  NAMING_CONVENTIONS = 'naming_conventions',
  ABSTRACT_CLASSES = 'abstract_classes',
  INTERFACES = 'interfaces',
  
  // API Unity & Scripting
  MONOBEHAVIOUR_LIFECYCLE = 'monobehaviour_lifecycle',
  INPUT_HANDLING = 'input_handling',
  GAMEOBJECT_MANIPULATION = 'gameobject_manipulation',
  COMPONENT_ACCESS = 'component_access',
  PHYSICS_RIGIDBODY = 'physics_rigidbody',
  VECTOR_MATH = 'vector_math',
  COROUTINES = 'coroutines',
  ANIMATION_SYSTEM = 'animation_system',
  UI_INTERACTION = 'ui_interaction',
  DEBUGGING = 'debugging',

  // Python/JS Fundamentals
  PYJS_PRINT_OUTPUT = 'pyjs_print_output',
  PYJS_USER_INPUT = 'pyjs_user_input',
  PYJS_STRINGS = 'pyjs_strings',
  PYJS_LISTS_ARRAYS = 'pyjs_lists_arrays',
  PYJS_DICTIONARIES = 'pyjs_dictionaries',
  PYJS_TYPE_CONVERSION = 'pyjs_type_conversion',

  // Godot GDScript
  GODOT_PRINT_OUTPUT = 'godot_print_output',
  GODOT_ARRAYS_DICTS = 'godot_arrays_dicts',
  GODOT_NODES_SCENES = 'godot_nodes_scenes',
  GODOT_LIFECYCLE = 'godot_lifecycle',
  GODOT_SIGNALS = 'godot_signals',
  GODOT_AWAIT_CALLABLES = 'godot_await_callables',
  GODOT_TYPED_GDSCRIPT = 'godot_typed_gdscript',
  GODOT_VECTOR_MATH = 'godot_vector_math',
  GODOT_SCENE_INSTANCING = 'godot_scene_instancing',
  GODOT_CHARACTER_MOVEMENT = 'godot_character_movement',
  GODOT_OOP_PATTERNS = 'godot_oop_patterns'
}

export enum FeedbackType {
  LOGIC_ANALYSIS = 'logic_analysis',
  COMPILATION_CHECK = 'compilation_check',
  BEST_PRACTICES = 'best_practices',
  IMPROVEMENTS = 'improvements'
}

// === Interfaces principales ===

export interface User {
  id: string;
  pseudonym: string;
  email?: string;
  role: 'student' | 'admin';
  createdAt: Date;
  lastActive: Date;
  emailVerifiedAt?: Date;
  isTeacher?: boolean; // Deprecated: use role === 'admin' instead
}

export interface ConceptProgress {
  concept: ConceptCategory;
  masteryLevel: number; // 0-100
  attemptCount: number;
  successCount: number;
  lastAttemptAt: Date;
}

export interface UserProgress {
  userId: string;
  concepts: ConceptProgress[];
  totalExercises: number;
  totalSuccesses: number;
  totalTimeSpent: number; // en minutes
  currentStreak: number;
  longestStreak: number;
  updatedAt: Date;
}

export interface Exercise {
  id: string;
  title: string;
  description: string;
  concepts: ConceptCategory[];
  difficulty: Difficulty;
  type: ExerciseType;
  prompt: string;
  context?: string; // Description de la structure Unity si nécessaire
  expectedSolution?: string;
  startingCode?: string; // Code de départ pré-rempli dans l'éditeur
  hints?: string[];
  learningOutcomes?: string[]; // Objectifs d'apprentissage
  commonMistakes?: CommonMistake[]; // Erreurs courantes
  successCriteria?: string[]; // Critères de réussite
  estimatedTimeMinutes: number;
  createdAt: Date;
  createdBy: 'ai' | string; // 'ai' ou id de l'enseignant
  // Ce qui a produit l'exercice dans le parcours : renvoyés par POST /api/exercises/generate et par la lecture d'un exercice
  knowledgePointIds?: string[];
  learningNodeId?: string;
  // Vrai si l'exercice a été publié comme quête, active ou terminée : la correction ne s'affiche alors qu'à partir de 90
  isQuest?: boolean;
}

export interface CommonMistake {
  mistake: string;
  explanation: string;
  example?: string;
}

export interface ExerciseAttempt {
  id: string;
  exerciseId: string;
  userId: string;
  solution: string;
  isCorrect: boolean;
  timeSpent: number; // en secondes
  submittedAt: Date;
  feedback?: ExerciseFeedback;
}

export interface ExerciseFeedback {
  id: string;
  attemptId: string;
  logicAnalysis: string;
  whatWorked: string[]; // Ce que l'étudiant a réussi
  keyIssues: string[]; // Problèmes principaux à corriger
  compilationStatus: {
    wouldCompile: boolean;
    errors: string[];
    warnings: string[];
    scoreCapped?: boolean; // A4 : plafond 60 appliqué (absent sur les anciens feedbacks stockés)
    notAnalyzed?: boolean; // copie identique au code de départ : rien n'a été analysé, rien à afficher
  };
  learningInsights: string[]; // Concepts à approfondir
  nextSteps: string[]; // Actions concrètes pour progresser
  overallScore: number; // 0-100
  conceptMastery: {
    [key in ConceptCategory]?: number; // 0-100 pour chaque concept abordé
  };
  correctSolution?: string; // Code de la solution correcte
  generatedAt: Date;
  
  // Legacy fields for backward compatibility
  bestPractices?: string[];
  improvements?: string[];
}

// === Interfaces pour les requêtes API ===

export interface GenerateExerciseRequest {
  userId: string;
  concepts?: ConceptCategory[];
  difficulty: Difficulty;
  customPrompt?: string;
  adaptToProgress?: boolean;
}

export interface SubmitSolutionRequest {
  exerciseId: string;
  userId: string;
  solution: string;
  originalCode?: string; // Starting template code for comparison
  timeSpent: number;
}

export interface ExerciseHistoryQuery {
  userId: string;
  page?: number;
  limit?: number;
  concepts?: ConceptCategory[];
  difficulty?: Difficulty;
  isCorrect?: boolean;
}

// === Interfaces pour les réponses API ===

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ExerciseWithAttempts extends Exercise {
  userAttempt?: ExerciseAttempt;
  totalAttempts: number;
  successRate: number;
}

// === Interfaces pour le dashboard enseignant ===

export interface ClassMetrics {
  totalStudents: number;
  activeStudents: number; // actifs dans les 7 derniers jours
  totalExercises: number;
  totalAttempts: number;
  averageSuccessRate: number;
  conceptDifficulty: {
    concept: ConceptCategory;
    averageSuccessRate: number;
    totalAttempts: number;
  }[];
  activityOverTime: {
    date: string;
    exerciseAttempts: number;
    uniqueStudents: number;
  }[];
}

export interface StudentSummary {
  user: User;
  progress: UserProgress;
  recentActivity: {
    lastExerciseAt: Date;
    exercisesThisWeek: number;
    currentStreak: number;
  };
}

// === Interfaces pour l'export ===

export interface ExerciseReport {
  exercise: Exercise;
  attempt: ExerciseAttempt;
  feedback: ExerciseFeedback;
  formattedReport: string;
}

// === Utilitaires et helpers ===

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface ConfigurablePrompt {
  template: string;
  variables: Record<string, string>;
  concepts: ConceptCategory[];
  difficulty: Difficulty;
}

// === Types pour l'intégration Gemini ===

export interface GeminiRequest {
  prompt: string;
  temperature?: number;
  maxTokens?: number;
  context?: string;
}

export interface GeminiResponse {
  text: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface DifficultySettings {
  userId: string;
  algorithmicComplexity: AlgorithmicComplexity; // Axe 1
  unityApiKnowledge?: UnityApiKnowledge;        // Axe 2 (Unity mode only)
  codeScope: CodeScope;                         // Axe 3
  cognitiveLoad: CognitiveLoad;                 // Axe 4
  updatedAt: Date;
}

// Axe 1: Complexité Algorithmique
export enum AlgorithmicComplexity {
  SEQUENTIAL = 0,     // Aucune logique
  BASIC = 1,          // if/else, boucles simples
  INTERMEDIATE = 2,   // Collections, recherche/tri
  ADVANCED = 3        // Algorithmes complexes, récursivité
}

// Axe 2: Connaissances de l'API Unity
export enum UnityApiKnowledge {
  FUNDAMENTAL = 1,    // GameObject, Transform, Debug.Log
  COMMON = 2,         // Rigidbody, Input, GetComponent
  SPECIFIC = 3        // Quaternion, Raycasting, Coroutines
}

// Axe 3: Étendue du Code (Scope)
export enum CodeScope {
  ONE_LINE = 0,       // Une seule ligne à modifier
  FUNCTION = 1,       // Remplir une fonction
  COMPONENT = 2,      // Écrire un script complet
  INTERACTION = 3     // Faire interagir plusieurs scripts
}

// Axe 4: Charge Cognitive
export enum CognitiveLoad {
  ISOLATED = 1,       // Un seul concept
  COMBINED = 2,       // 2-3 concepts
  INTEGRATED = 3      // Plusieurs systèmes interdépendants
}

// Axe 5: Profondeur de la Connaissance (contextuel au sujet)
export enum TopicDepth {
  BASIC = 'basic',
  ADVANCED = 'advanced'
}

export interface TopicWithDepth {
  name: string;
  displayName: string;
  depth: TopicDepth;
  concepts: ConceptCategory[];
}

/**
 * LANGUAGE SUPPORT NOTICE:
 * ExerciseGenerationRequest now includes language parameter for bilingual exercise generation.
 *
 * language: Determines the language for generated exercise content:
 * - Exercise title, description, and instructions
 * - Code comments and explanations
 * - Learning outcomes and success criteria
 *
 * The language parameter should be passed from the user's preferredLanguage setting.
 */
export interface ExerciseGenerationRequest {
  userId: string;
  // LANGUAGE: Target language for exercise generation
  language?: Language;
  // LEARNING MODE: Programming context (unity_csharp, python_js, godot)
  learningMode?: LearningMode;
  programmingLanguage?: ProgrammingLanguage;
  // Axes 1-4 viennent des paramètres utilisateur
  algorithmicComplexity?: AlgorithmicComplexity;
  unityApiKnowledge?: UnityApiKnowledge;
  codeScope?: CodeScope;
  cognitiveLoad?: CognitiveLoad;
  // Axe 5 choisi au moment de la génération
  specificTopic?: TopicWithDepth;
  // Paramètres existants
  concepts?: ConceptCategory[];
  difficulty?: Difficulty;
  adaptToProgress?: boolean;
  customPrompt?: string;
  // B7 : sujet précis de la demande (ex. tags de la question de certif pour « Challenge me on this »).
  // Quand présent, la validation de couverture B5 et son retry portent sur CE sujet plutôt que sur
  // le customPrompt complet (un customPrompt contextuel long rendrait la validation inopérante).
  customPromptTopic?: string;
  // « Augmenter la difficulté » ou « Même niveau, autre situation » : l'exercice d'où part l'étudiant. Le serveur
  // construit la consigne à partir de son énoncé et de sa solution (CHARTE-PEDAGOGIQUE-PROG2D.md, §2.2).
  followUp?: ExerciseFollowUp;
}

/** 'next' = « Augmenter la difficulté » : même sujet, un cran plus loin ; 'continue' = « Même niveau, autre situation ». */
export type FollowUpKind = 'next' | 'continue';

export interface ExerciseFollowUp {
  kind: FollowUpKind;
  previousExerciseId: string;
}

export interface DifficultyLabels {
  algorithmicComplexity: {
    [AlgorithmicComplexity.SEQUENTIAL]: string;
    [AlgorithmicComplexity.BASIC]: string;
    [AlgorithmicComplexity.INTERMEDIATE]: string;
    [AlgorithmicComplexity.ADVANCED]: string;
  };
  unityApiKnowledge: {
    [UnityApiKnowledge.FUNDAMENTAL]: string;
    [UnityApiKnowledge.COMMON]: string;
    [UnityApiKnowledge.SPECIFIC]: string;
  };
  codeScope: {
    [CodeScope.ONE_LINE]: string;
    [CodeScope.FUNCTION]: string;
    [CodeScope.COMPONENT]: string;
    [CodeScope.INTERACTION]: string;
  };
  cognitiveLoad: {
    [CognitiveLoad.ISOLATED]: string;
    [CognitiveLoad.COMBINED]: string;
    [CognitiveLoad.INTEGRATED]: string;
  };
}

// Sujets prédéfinis avec leurs profondeurs
export interface PredefinedTopic {
  name: string;
  displayName: string;
  icon: string;
  concepts: ConceptCategory[];
  depths: {
    basic: {
      displayName: string;
      description: string;
    };
    advanced: {
      displayName: string;
      description: string;
    };
  };
}

// Pedagogical Chatbot Types
export interface ChatMessage {
  id: string;
  exerciseId: string;
  userId: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  context?: ChatContext;
  suggestions?: string[];
}

/**
 * LANGUAGE SUPPORT NOTICE:
 * ChatContext now includes language parameter for bilingual chat responses.
 *
 * language: Determines the language for chatbot responses:
 * - AI explanations and guidance
 * - Code comments and suggestions
 * - Mini-course content generation
 *
 * This ensures consistent language throughout the chat session.
 */
export interface ChatContext {
  exerciseTitle: string;
  exerciseDescription: string;
  concepts: ConceptCategory[];
  language?: Language;
  learningMode?: LearningMode;
  programmingLanguage?: ProgrammingLanguage;
  currentCode?: string;
  feedback?: ExerciseFeedback;
  miniCoursGenerated?: boolean;
  correctionViewed?: boolean;
  userDifficultySettings?: DifficultySettings;
  /** B4 — mode « explication guidée » (dernier recours) : une instruction à la fois, localisée dans le code */
  guidedMode?: boolean;
}

export interface ChatSession {
  id: string;
  exerciseId: string;
  userId: string;
  messages: ChatMessage[];
  isActive: boolean;
  lastActivity: Date;
  createdAt: Date;
}

export interface ChatSuggestion {
  id: string;
  text: string;
  category: 'concept' | 'syntax' | 'unity' | 'debugging' | 'improvement';
  icon: string;
  priority: number;
}

// === Quest System Types ===

export interface Quest {
  id: string;
  exerciseId: string;
  title: string;
  createdBy: string;
  status: 'active' | 'scheduled' | 'expired';
  scheduledAt?: Date;
  activatedAt?: Date;
  expiredAt?: Date;
  createdAt: Date;
}

export interface QuestParticipation {
  id: number;
  questId: string;
  userId: string;
  username: string; // User's pseudonym
  attemptId: string;
  score: number;
  completedQuest: boolean;
  submittedAt: Date;
  attemptCount?: number; // Number of attempts by this user
}

export interface QuestDetails extends Quest {
  exercise: Exercise;
  participations: QuestParticipation[];
  totalParticipants: number;
  completionRate: number;
  averageScore: number;
}

export interface CreateQuestRequest {
  exerciseId: string;
  title: string;
  schedule: 'now' | 'later';
  scheduledAt?: string; // ISO date string
}

/**
 * LANGUAGE SUPPORT NOTICE:
 * UserSettings now includes language preference for exercise generation.
 *
 * preferredLanguage: Determines the language for:
 * - Exercise generation (title, description, instructions)
 * - AI feedback and corrections
 * - Chatbot responses
 * - Mini-course content
 *
 * UI elements (buttons, labels, navigation) remain in French regardless of this setting.
 */
export interface UserSettings {
  showCorrectionButton: boolean;
  preferredLanguage?: Language; // Made optional to prevent breaking existing functionality
  learningMode?: LearningMode;
  programmingLanguage?: ProgrammingLanguage;
}

// === Quest Constants ===

export const QUEST_CORRECTION_THRESHOLD = 90;
export const GENERAL_CORRECTION_THRESHOLD = 90;
/** Quêtes actives en même temps : au-delà, la plus anciennement activée se termine. */
export const MAX_ACTIVE_QUESTS = 3;

// Quest status enum
export enum QuestStatus {
  ACTIVE = 'active',
  SCHEDULED = 'scheduled',
  EXPIRED = 'expired'
}

// === Quest System Types ===

export interface Quest {
  id: string;
  exerciseId: string;
  title: string;
  createdBy: string;
  status: 'active' | 'scheduled' | 'expired';
  scheduledAt?: Date;
  activatedAt?: Date;
  expiredAt?: Date;
  createdAt: Date;
}

export interface QuestParticipation {
  id: number;
  questId: string;
  userId: string;
  username: string; // User's pseudonym
  attemptId: string;
  score: number;
  completedQuest: boolean;
  submittedAt: Date;
  attemptCount?: number; // Number of attempts by this user
}

export interface QuestDetails extends Quest {
  exercise: Exercise;
  participations: QuestParticipation[];
  totalParticipants: number;
  completionRate: number; // Percentage of participants who completed
  averageScore: number;
}
